/**
 * Turns the balance's `pools` array into the rows the credits panel draws (#724):
 * every pool the user can spend from — their own, plus each team they're an active
 * member of — with the one their AI usage is billed to flagged.
 *
 * Kept out of the directive for the same reason ai-credits-refresh-gate.ts is: the
 * directive needs a DOM and a digest to exercise, while the decisions worth pinning
 * down here are pure — whether a list is worth showing at all, whether a pool has an
 * allotment worth naming, whether a pool is out of credits.
 *
 * The wire types are pulled in with `import type`, so this module has no runtime
 * dependency on anything: the spec loads it on its own, with no Angular and no bundler.
 */
import type { AiCreditPool } from '#gp/app-home/services/ai-credits-contract';

/**
 * How the personal pool is named in the list. The server deliberately sends `name:
 * null` for it (only teams have names), leaving the wording to the client.
 */
export const PERSONAL_POOL_NAME = 'Your personal credits';

/**
 * Fallback for a team pool that arrives without a name. A team always has one, so
 * this is belt-and-braces — but an unlabelled row in a list about *where your money
 * goes* would be worse than a generic label.
 */
export const UNNAMED_TEAM_POOL_NAME = 'Your team';

/** One rendered pool row. Purely presentational: no formatting, no wire fields the panel doesn't use. */
export interface AiCreditPoolRow {
    /** Unique per pool (owner type + id) — the `track by` key for the repeat. */
    key: string;
    /** The team's name, or the personal pool's client-chosen label. */
    name: string;
    /** True for the user's own pool, false for a team's. */
    isPersonal: boolean;
    /**
     * True on the single pool this user's AI usage is currently charged to, which is
     * also the pool every other figure on the panel describes.
     */
    isCurrent: boolean;
    /**
     * Whether this pool has an allotment at all. False means the row must say NOTHING
     * about an allotment — not "0 allotted credits" — because a pool that has never
     * been granted a plan allotment does not have an empty one (#724's Goal states
     * this explicitly).
     */
    hasAllotment: boolean;
    /**
     * Allotment credits left, never below zero. Only meaningful when
     * {@link hasAllotment}.
     *
     * Clamped because a pool really can go negative: metering posts after a stream
     * finishes, and the server's SpendOrder puts the part it could not fund on the
     * allotment (FunctionsPoolOwnerResolverTests.A_team_pool_in_overage_counts_as_dry
     * pins that). Rendered raw, the row read "-1,240 allotted credits left." — a debt
     * phrased as a positive. {@link isEmpty} carries the overdrawn state instead, and
     * it is computed from the unclamped figures so an overage still counts as dry.
     */
    allotmentRemaining: number;
    /** Whether any purchased credits have ever been added to this pool. */
    hasPurchased: boolean;
    /** Purchased credits left, out of {@link purchasedTotal}; clamped like {@link allotmentRemaining}. */
    purchasedRemaining: number;
    /** All-time purchased credits (paid top-ups + promotional grants). */
    purchasedTotal: number;
    /**
     * The pool has held credits and has none left to spend. This is the state that
     * makes the billed pool understandable when it isn't the first team in the list:
     * spend prefers a team pool that still has credits and falls through the dry ones.
     */
    isEmpty: boolean;
    /** The pool has never held credits of either kind — nothing to report but its existence. */
    isUnfunded: boolean;
}

/**
 * The part of the balance this module reads. Structural, and `pools` is optional on
 * purpose: a browser holding a page from a server that predates #724 gets a response
 * with no pools at all, and that must degrade to "no list", never to an exception.
 */
export interface AiCreditPoolListSource {
    pools?: AiCreditPool[];
}

/** Map the server's pools to rows, preserving the order the server sent them in. */
export function buildPoolRows(balance: AiCreditPoolListSource | null | undefined): AiCreditPoolRow[] {
    const pools = (balance && balance.pools) || [];

    return pools.filter(pool => !!pool).map(toRow);
}

/**
 * Whether the pool list is worth drawing.
 *
 * Only when there's more than one pool. The overwhelming majority of users have no
 * teams, so their list would be a single row restating the figures immediately above
 * it — noise on a panel that is already dense. One pool means there is no question
 * about where usage is charged, which is the only question the list exists to answer.
 * An older server's missing `pools` yields no rows and so falls out of the same rule,
 * leaving the panel exactly as it was before #724.
 */
export function shouldShowPoolList(rows: AiCreditPoolRow[]): boolean {
    return rows.length > 1;
}

/**
 * The headline figures, summed over every pool the user can draw on (#724).
 *
 * Before this, the percentages at the top of the panel described only the pool being
 * charged, which made the page contradict itself for anyone on a team: it announced
 * "0 of 1,999 allotted credits used" while the row directly below said the same pool
 * had 3,753 left. Those were two different quantities — what Stripe granted into the
 * current billing period, against what the pool actually holds — and neither was the
 * question being asked, which is how much credit this person has in total.
 */
export interface AiCreditPoolTotals {
    /** Allotment spent across every pool, in each pool's current cycle. */
    allotmentUsed: number;
    /** What those cycles made available in total — the bar's denominator. */
    allotmentTotal: number;
    /** False when no pool has an allotment at all, so the panel shows no allotment bar. */
    hasAllotment: boolean;
    /** Purchased credits spent across every pool, all-time. */
    purchasedUsed: number;
    /** Purchased credits ever added across every pool. */
    purchasedTotal: number;
    /** False when nothing has ever been purchased anywhere, so no purchased bar. */
    hasPurchased: boolean;
}

/**
 * What the totals are read from. The top-level fields are the fallback: a browser
 * holding a bundle newer than its server gets a response with no `pools`, and the
 * panel must still draw the billed pool's figures rather than a row of zeros.
 */
export interface AiCreditPoolTotalsSource extends AiCreditPoolListSource {
    monthlyAllotment?: number;
    allotmentUsed?: number;
    purchasedUsed?: number;
    purchasedTotal?: number;
}

export function totalAcrossPools(balance: AiCreditPoolTotalsSource | null | undefined): AiCreditPoolTotals {
    const pools = (balance && balance.pools) || [];

    if (!pools.length) {
        // No pool list: fall back to the billed pool's own figures, which is exactly
        // what the panel showed before #724.
        const allotmentTotal = (balance && balance.monthlyAllotment) || 0;
        const purchasedTotal = (balance && balance.purchasedTotal) || 0;

        return {
            allotmentUsed: (balance && balance.allotmentUsed) || 0,
            allotmentTotal,
            hasAllotment: allotmentTotal > 0,
            purchasedUsed: (balance && balance.purchasedUsed) || 0,
            purchasedTotal,
            hasPurchased: purchasedTotal > 0,
        };
    }

    let allotmentUsed = 0;
    let allotmentTotal = 0;
    let hasAllotment = false;
    let purchasedUsed = 0;
    let purchasedTotal = 0;

    pools.filter(pool => !!pool).forEach(pool => {
        // A pool with no allotment contributes nothing to either side rather than a
        // zero denominator — and `hasAllotment` is what decides the bar exists at all,
        // so a user whose only credits are purchased still sees no empty allotment bar.
        if (pool.hasAllotment) {
            hasAllotment = true;
            allotmentUsed += pool.allotmentUsed || 0;
            allotmentTotal += pool.allotmentTotal || 0;
        }

        purchasedUsed += pool.purchasedUsed || 0;
        purchasedTotal += pool.purchasedTotal || 0;
    });

    return {
        // Clamped: a pool can be overdrawn, and neither a negative "used" nor a
        // negative denominator is a sentence the panel can render.
        allotmentUsed: Math.max(0, allotmentUsed),
        allotmentTotal: Math.max(0, allotmentTotal),
        hasAllotment: hasAllotment && allotmentTotal > 0,
        purchasedUsed: Math.max(0, purchasedUsed),
        purchasedTotal: Math.max(0, purchasedTotal),
        hasPurchased: purchasedTotal > 0,
    };
}

function toRow(pool: AiCreditPool): AiCreditPoolRow {
    // `|| 0` throughout, as the rest of this panel does: an older or partial response
    // must render as a zero figure, never as "NaN credits".
    const allotmentRemaining = pool.allotmentRemaining || 0;
    const purchasedRemaining = pool.purchasedRemaining || 0;
    const purchasedTotal = pool.purchasedTotal || 0;
    const hasAllotment = !!pool.hasAllotment;
    const hasPurchased = purchasedTotal > 0;

    // Allotment + purchased is exactly the server's PoolBalances.Spendable, the same
    // quantity CreditPoolSelection tests when it decides which pool to charge. So a row
    // shown as having nothing left is dry in precisely the sense that made spend skip it.
    // Computed from the RAW figures: an overdrawn pool must still count as dry here,
    // even though the figures the row displays are clamped at zero.
    const spendable = allotmentRemaining + purchasedRemaining;

    return {
        key: pool.ownerType + ':' + pool.ownerId,
        name: poolName(pool),
        // Anything that isn't a team is the user's own pool: an unrecognised owner type
        // should read as personal rather than silently claim to be a team's shared money.
        isPersonal: pool.ownerType !== 'team',
        isCurrent: !!pool.isCurrent,
        hasAllotment,
        // Never below zero: the row says "… credits left", and a negative left is a
        // sentence that reads as a positive one. See the field docs above.
        allotmentRemaining: Math.max(0, allotmentRemaining),
        hasPurchased,
        purchasedRemaining: Math.max(0, purchasedRemaining),
        purchasedTotal,
        isEmpty: (hasAllotment || hasPurchased) && spendable <= 0,
        isUnfunded: !hasAllotment && !hasPurchased,
    };
}

function poolName(pool: AiCreditPool): string {
    if (pool.ownerType !== 'team') return PERSONAL_POOL_NAME;

    return (pool.name || '').trim() || UNNAMED_TEAM_POOL_NAME;
}
